import { useCallback, useEffect, useRef, useState } from 'react'
import {
  buildVehicleNotification,
  eventKind,
  notificationKind,
  testNotification,
  vehicleName,
} from '../utils'
import { useBrowserNotifications } from './useBrowserNotifications'
import { useVehiclesService } from './useVehiclesService'

const POLL_INTERVAL_MS = 60 * 1000
const ALARM_COOLDOWN_MS = 60 * 1000
const HISTORY_WINDOW_MS = 2 * 24 * 60 * 60 * 1000
const SECONDS_LIMIT = 1e12

const toMilliseconds = (value) =>
  value < SECONDS_LIMIT ? Math.round(value * 1000) : value

const toHistoryItem = (vehicle, item) => {
  const body = item.alert_msg || item.body || item.message
  if (!body) return null
  const timestamp = toMilliseconds(Number(item.timestamp))
  return {
    key: `${vehicle.value}-${item.timestamp}-${body}`,
    title: vehicleName(vehicle),
    vehicle: vehicleName(vehicle),
    kind: notificationKind(body),
    body,
    timestamp: Number.isFinite(timestamp) ? timestamp : Date.now(),
  }
}

const byNewest = (a, b) => b.timestamp - a.timestamp

const useVehicleNotifications = (vehicles) => {
  const { permission, requestPermission, notify } = useBrowserNotifications()
  const { getNotifications } = useVehiclesService()
  const getNotificationsRef = useRef(getNotifications)
  getNotificationsRef.current = getNotifications
  const lastAlarmAt = useRef({})
  const seenKeys = useRef(new Set())
  const startedAt = useRef(Date.now())
  const apiForbidden = useRef(false)
  const [history, setHistory] = useState([])
  const [lastReadAt, setLastReadAt] = useState(startedAt.current)

  // El historial no depende de que el navegador muestre avisos: sirve a quien no le llegan.
  const addToHistory = useCallback((items) => {
    const fresh = items.filter((item) => !seenKeys.current.has(item.key))
    fresh.forEach((item) => seenKeys.current.add(item.key))
    if (fresh.length > 0) {
      setHistory((prev) => [...fresh, ...prev].sort(byNewest))
    }
    return fresh
  }, [])

  // El dispositivo puede repetir el mensaje de alarma: se avisa máximo una vez por minuto.
  const handleVehicleEvent = useCallback(
    (event) => {
      const notification = buildVehicleNotification(event)
      if (!notification) return
      const now = Date.now()
      if (event.type === 'alarm') {
        if (now - (lastAlarmAt.current[notification.tag] || 0) < ALARM_COOLDOWN_MS) {
          return
        }
        lastAlarmAt.current[notification.tag] = now
      }
      addToHistory([
        {
          key: `${notification.tag}-${now}`,
          title: notification.title,
          vehicle: vehicleName(event.vehicle),
          kind: eventKind(event.type),
          body: notification.body,
          timestamp: now,
        },
      ])
      notify(notification)
    },
    [addToHistory, notify]
  )

  // Mismas notificaciones que la app móvil, de todas las motos de la cuenta.
  const syncNotifications = useCallback(
    async (since, { notifyNew }) => {
      if (apiForbidden.current) return
      const results = await Promise.all(
        vehicles.map(async (vehicle) => {
          const items = await getNotificationsRef.current(since, vehicle.value)
          if (items === null) {
            apiForbidden.current = true
            return []
          }
          return items.map((item) => toHistoryItem(vehicle, item))
        })
      )
      const fresh = addToHistory(results.flat().filter(Boolean))
      if (!notifyNew) return
      fresh
        .filter((item) => item.timestamp >= startedAt.current)
        .forEach((item) =>
          notify({ title: item.title, body: item.body, tag: item.key })
        )
    },
    [vehicles, addToHistory, notify]
  )

  useEffect(() => {
    if (vehicles.length === 0) return undefined
    syncNotifications(startedAt.current - HISTORY_WINDOW_MS, {
      notifyNew: false,
    })
    const intervalId = setInterval(
      () => syncNotifications(startedAt.current, { notifyNew: true }),
      POLL_INTERVAL_MS
    )
    return () => clearInterval(intervalId)
  }, [vehicles, syncNotifications])

  const refreshHistory = useCallback(
    () =>
      syncNotifications(Date.now() - HISTORY_WINDOW_MS, { notifyNew: false }),
    [syncNotifications]
  )

  const markAsRead = useCallback(() => setLastReadAt(Date.now()), [])

  const unreadCount = history.filter((item) => item.timestamp > lastReadAt)
    .length

  const enableNotifications = useCallback(async () => {
    const result = await requestPermission()
    if (result === 'granted') await notify(testNotification)
    return result
  }, [requestPermission, notify])

  return {
    permission,
    enableNotifications,
    handleVehicleEvent,
    history,
    unreadCount,
    lastReadAt,
    refreshHistory,
    markAsRead,
  }
}

export { useVehicleNotifications }
